// A paired rower or bike logs its own pieces in free training (#158).
//
// Fed the machine's readings a few times a second, this spots a piece: the machine
// starts moving (power, cadence or stroke rate above zero), then goes still
// for a few seconds after real work. The piece's distance is what the machine
// counted while it moved, so a monitor that zeroes its distance for a new
// piece (the Concept2 PM5 does) reads the same as one that keeps counting.
//
// A machine that counts distance moves only when its distance goes up. A PM5
// that finishes a piece keeps sending the last stroke rate and watts, so going
// by those alone a piece never ended (a 2k was never logged).
//
// Pure and React-free, so it can be unit-tested.

export const IDLE_MS = 5000; // still this long ends a piece
export const MIN_PIECE_M = 100; // shorter than this, and…
export const MIN_PIECE_MS = 30000; // …shorter than this, isn't a piece

const moving = (r) =>
  !!r && ((Number(r.power_watts) || 0) > 0 || (Number(r.cadence_rpm) || 0) > 0 || (Number(r.stroke_rate_spm) || 0) > 0);

/**
 * @returns {{ push(reading: object|null, nowMs: number): null | { m: number|null, s: number } }}
 *   `push` takes the latest reading (null when the machine has gone quiet) and
 *   returns a finished piece, once, when one ends.
 */
export function createPieceDetector({ idleMs = IDLE_MS, minM = MIN_PIECE_M, minMs = MIN_PIECE_MS } = {}) {
  let piece = null; // { startMs, lastMoveMs, metres, lastDist }
  let countsDistance = false; // the machine has reported a distance
  let prevDist = null; // its last distance, piece or not…
  let prevMs = null; // …and when it came
  return {
    push(reading, nowMs) {
      const dist = Number(reading?.total_distance_m);
      const hasDist = !!reading && reading.total_distance_m != null && Number.isFinite(dist);
      if (hasDist) countsDistance = true;
      const advanced = hasDist && prevDist != null && dist > prevDist;
      const from = { dist: prevDist, ms: prevMs };
      if (hasDist) {
        prevDist = dist;
        prevMs = nowMs;
      }
      if (countsDistance ? advanced : moving(reading)) {
        // Moving by distance means it was already moving at the last reading.
        if (!piece) {
          piece = advanced
            ? { startMs: from.ms, lastMoveMs: nowMs, metres: 0, lastDist: from.dist }
            : { startMs: nowMs, lastMoveMs: nowMs, metres: 0, lastDist: hasDist ? dist : null };
        }
        piece.lastMoveMs = nowMs;
      }
      if (piece && hasDist) {
        // Only forward steps count: a monitor that resets to 0 doesn't undo
        // what was rowed.
        if (piece.lastDist != null && dist > piece.lastDist) piece.metres += dist - piece.lastDist;
        piece.lastDist = dist;
      }
      if (piece && nowMs - piece.lastMoveMs >= idleMs) {
        const done = piece;
        piece = null;
        const ms = done.lastMoveMs - done.startMs;
        if (done.metres < minM && ms < minMs) return null;
        return { m: done.metres >= minM ? Math.round(done.metres) : null, s: Math.max(1, Math.round(ms / 1000)) };
      }
      return null;
    },
  };
}

/** The log entry's name for a machine type. */
export const machineName = (type) => (type === "rower" ? "Rower" : "Bike");
